import { useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import {
  setColleagueRelationship,
  type ColleagueRelationship,
  type OneOnOneColleagueItem,
} from '@/lib/colleagues.ts';
import { patchColleague } from './patchColleague.ts';

export interface RelationshipChange {
  colleague: OneOnOneColleagueItem;
  relationship: ColleagueRelationship;
}

/**
 * Who a colleague is to the user: team member, manager or other. Shown the
 * moment it is picked, on the Team page and their own page alike.
 */
export function useColleagueRelationship() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ colleague, relationship }: RelationshipChange) => {
      const user = await queryClient.ensureQueryData({
        queryKey: ['currentUser'],
        queryFn: fetchCurrentUser,
      });
      await setColleagueRelationship(user.id, colleague.id, relationship);
    },
    onMutate: ({ colleague, relationship }) =>
      patchColleague(queryClient, colleague.id, { relationship }),
    /* Settled rather than successful: the re-read is what reverts a choice the server refused. */
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['oneOnOneColleagues'] });
      await queryClient.invalidateQueries({ queryKey: ['oneOnOneColleague'] });
    },
  });
}
